/**
 * payerNamesAgree.js — R23 · TEST 1.01 (Dr. Rami 2026-10-03), patient P1 step 8.
 * Can the insurer name read off one side of a card name the payer the OTHER side's printed payer
 * ID resolved to?
 *
 * ★★★ THE GAP THE SERVER CANNOT CLOSE ALONE. orella-api D5b step 2 (fc73dff, not live) asks the
 *   patient to confirm when the payer resolved from a card-printed payer ID is not named by an
 *   insurer name read IN THE SAME REQUEST (that image's own name, or an optional `frontProvider`).
 *   This app scans the two sides in two requests and sends no front name with the back, and either
 *   side may be first; so a FRONT naming one insurer over a BACK printing another insurer's payer
 *   ID reaches the server as two separate, agreeing answers. useCardScanPair has both answers once
 *   both sides are in; it asks this function, and on a disagreement sets the existing confirm flag
 *   (fc73dff's message names this client follow-up).
 *
 * ★★ THE RULE IS THE SERVER'S, PORTED BY HAND (rcm_service.payer_names_agree, _payer_tokens,
 *   _PAYER_STOPWORDS, _GENERIC_PAYER_TOKENS, _GEO_PAYER_TOKENS at fc73dff; the repos share no
 *   package): true when no name was read, when the compact alphanumerics are equal, or when the
 *   non-generic tokens of one name all appear in the other ('Health Net' vs 'Health Net Claims
 *   California'). Stricter than a shared word on purpose: 'Blue Shield' and 'Anthem Blue Cross'
 *   do NOT agree. A false "disagree" costs one tap; a false "agree" routes the claim to the wrong
 *   insurer without asking. scripts/check-payer-id-confirm.mjs holds the port to a table the REAL
 *   Python function produced.
 *
 * Pure (no imports).
 */
const STOPWORDS = new Set(['insurance', 'inc', 'llc', 'the', 'of', 'co', 'company', 'corp',
  'corporation', 'plan', 'plans', 'group', 'assurance'])
const GENERIC = ['health', 'healthcare', 'care', 'medical', 'medicine',
  'benefit', 'benefits', 'network', 'networks', 'service', 'services',
  'system', 'systems', 'choice', 'advantage', 'partners', 'associates',
  'national', 'america', 'american', 'community', 'family',
  'premier', 'select', 'total', 'complete', 'general', 'first', 'life']
const GEO = ['alabama', 'alaska', 'arizona', 'arkansas', 'california', 'colorado',
  'connecticut', 'delaware', 'florida', 'georgia', 'hawaii', 'idaho',
  'illinois', 'indiana', 'iowa', 'kansas', 'kentucky', 'louisiana', 'maine',
  'maryland', 'massachusetts', 'michigan', 'minnesota', 'mississippi',
  'missouri', 'montana', 'nebraska', 'nevada', 'hampshire', 'jersey',
  'mexico', 'york', 'carolina', 'dakota', 'ohio', 'oklahoma', 'oregon',
  'pennsylvania', 'rhode', 'island', 'tennessee', 'texas', 'utah', 'vermont',
  'virginia', 'washington', 'wisconsin', 'wyoming', 'columbia',
  'northern', 'southern', 'eastern', 'western', 'central', 'north', 'south',
  'east', 'west', 'valley', 'coast', 'county', 'state', 'states']
const WEAK = new Set([...GENERIC, ...GEO])

const tokens = (name) => String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ')
  .split(' ').filter((t) => t && !STOPWORDS.has(t))
const compact = (x) => String(x || '').toLowerCase().replace(/[^a-z0-9]/g, '')
const strong = (name) => new Set(tokens(name).filter((t) => !WEAK.has(t)))
const within = (a, b) => [...a].every((t) => b.has(t))

/** `payer` is { displayName, payerName?, aliases? } — the resolved payer's names. */
export function payerNamesAgree(readName, payer) {
  const read = typeof readName === 'string' ? readName.trim() : ''
  if (!read) return true
  if (!payer || typeof payer !== 'object') return false
  const names = [payer.displayName, payer.payerName, ...(Array.isArray(payer.aliases) ? payer.aliases : [])]
  const rt = strong(read)
  const readAll = new Set(tokens(read))
  for (const n of names) {
    if (typeof n !== 'string' || !n.trim()) continue
    if (compact(n) === compact(read)) return true
    const nt = strong(n)
    if (rt.size && nt.size && (within(rt, new Set(tokens(n))) || within(nt, readAll))) return true
  }
  return false
}
